import { FC, useContext } from "react";
import { GapByCountyStatusBreakdown } from "@app/network-portfolio-management/interfaces";
import { MembershipToggleContext } from "@app/network-portfolio-management/hooks/useMembershipToggle";
import { formatCount } from "@app/network-portfolio-management/utils/formatUtils";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { DashCell } from "@app/network-portfolio-management/components/project-table/ProjectTableCell";

interface GapOverviewCellProps {
  colorModifier: "active" | "approved" | "opportunity" | "pending";
  hasError: boolean;
  isLoading: boolean;
  value: number | null;
}

// Gap by County Status breakdown, matching the card's GapByCountyStatus component — same
// ---/zero rule (null → dash, 0 is a real value), the same 4 status colors, and the same shimmer
// while its own independently-batched query is in flight, so a still-loading/errored fetch never
// reads as a valid unsupported-status dash. Kept in its own file (not ProjectTableCell.tsx, unlike
// most other single-line cells) since this cluster needs its own props interface, config table and
// factory rather than a one-line FC — colocating those with the many flat cells in
// ProjectTableCell.tsx was making that file harder to scan.
const GapOverviewCell: FC<GapOverviewCellProps> = ({ colorModifier, hasError, isLoading, value }) => {
  if (hasError) {
    return (
      <TooltipWrapper text="Unable to load. Please try again.">
        <DashCell />
      </TooltipWrapper>
    );
  }

  if (isLoading) {
    return (
      <div className="skeleton shimmer">
        <div className="bone network-portfolio-management-project-table__gap-overview-num-bone" />
      </div>
    );
  }

  return value == null ? (
    <DashCell />
  ) : (
    <span className={`network-portfolio-management-project-table__gap-overview-num--${colorModifier}`}>
      {formatCount(value)}
    </span>
  );
};

interface GapOverviewStatusConfig {
  colorModifier: "active" | "approved" | "opportunity" | "pending";
  countKey: keyof GapByCountyStatusBreakdown;
}

// Same 4-entry shape as the card's GapByCountyStatus.tsx STATUS_KEYS - kept as a separate constant
// rather than shared with it because tanstack-table needs 4 distinct exported Cell component
// references below (one per column), not a single value this file could .map() over like the card does.
const GAP_OVERVIEW_STATUS_KEYS: readonly GapOverviewStatusConfig[] = [
  { colorModifier: "active", countKey: "active" },
  { colorModifier: "pending", countKey: "pending" },
  { colorModifier: "approved", countKey: "approved" },
  { colorModifier: "opportunity", countKey: "opportunity" },
];

const createGapOverviewCell = ({ colorModifier, countKey }: GapOverviewStatusConfig): FC => function innerCreateGapOverviewCell() {
  const { gapByCountyStatus, gapByCountyStatusHasError, isGapByCountyStatusLoading } = useContext(MembershipToggleContext);
  return (
    <GapOverviewCell
      colorModifier={colorModifier}
      hasError={!!gapByCountyStatusHasError}
      isLoading={!!isGapByCountyStatusLoading}
      value={gapByCountyStatus?.[countKey] ?? null}
    />
  );
};

export const GapOverviewActiveCell = createGapOverviewCell(GAP_OVERVIEW_STATUS_KEYS[0]);
export const GapOverviewPendingCell = createGapOverviewCell(GAP_OVERVIEW_STATUS_KEYS[1]);
export const GapOverviewApprovedCell = createGapOverviewCell(GAP_OVERVIEW_STATUS_KEYS[2]);
export const GapOverviewOpportunityCell = createGapOverviewCell(GAP_OVERVIEW_STATUS_KEYS[3]);
